import * as THREE from 'three';
import { hash2 } from '../core/noise';
import {
  BIOME_NAMES,
  BIOME_PROFILES,
  biomeWeightsAt,
  type BiomeWeights,
} from '../world/biomes';

export const WEATHER_PARTICLE_COUNT = 224;

const MIN_VISIBLE_PARTICLES = 160;
const PARTICLE_VOLUME_RADIUS = 52;
const PARTICLE_VOLUME_HEIGHT = 31;
const PARTICLE_BOTTOM = -2.5;
const SNAPSHOT_TIME = 3.75;
const TAU = Math.PI * 2;

const WEATHER_COLORS = {
  lanternwood: [1, 0.968, 0.88],
  blueIce: [0.69, 0.94, 1],
  timberline: [0.91, 0.94, 0.98],
} as const;

const POINT_SIZES = {
  lanternwood: 1.48,
  blueIce: 0.72,
  timberline: 1.62,
} as const;

const FALL_SPEEDS = {
  lanternwood: 2.75,
  blueIce: 0.86,
  timberline: 1.18,
} as const;

export interface WeatherTarget {
  /** Blended value from the biome weather profiles. */
  readonly snowfall: number;
  /** Blended value from the biome weather profiles. */
  readonly wind: number;
  /** Number of particles submitted in the single weather draw call. */
  readonly visibleParticles: number;
  readonly pointSize: number;
  readonly fallSpeed: number;
  readonly horizontalSpeed: number;
  readonly opacity: number;
  readonly sparkle: number;
  readonly streak: number;
  readonly color: readonly [red: number, green: number, blue: number];
}

export interface WeatherController {
  /**
   * Updates weather for virtual world coordinates while particles stay near
   * the floating render origin. `snap` fixes animation time for screenshots.
   */
  update(x: number, z: number, delta?: number, snap?: boolean): void;
  dispose(): void;
}

interface MutableWeatherState {
  snowfall: number;
  wind: number;
  visibleParticles: number;
  pointSize: number;
  fallSpeed: number;
  horizontalSpeed: number;
  opacity: number;
  sparkle: number;
  streak: number;
  color: THREE.Color;
}

function weightedValue(
  weights: BiomeWeights,
  values: Readonly<Record<(typeof BIOME_NAMES)[number], number>>,
): number {
  let result = 0;
  for (const name of BIOME_NAMES) result += weights[name] * values[name];
  return result;
}

export function weatherTarget(weights: BiomeWeights): WeatherTarget {
  let snowfall = 0;
  let wind = 0;
  const color: [number, number, number] = [0, 0, 0];

  for (const name of BIOME_NAMES) {
    const amount = weights[name];
    const profile = BIOME_PROFILES[name].weather;
    snowfall += amount * profile.snowfall;
    wind += amount * profile.wind;
    color[0] += amount * WEATHER_COLORS[name][0];
    color[1] += amount * WEATHER_COLORS[name][1];
    color[2] += amount * WEATHER_COLORS[name][2];
  }

  return {
    snowfall,
    wind,
    visibleParticles: Math.min(
      WEATHER_PARTICLE_COUNT,
      Math.round(MIN_VISIBLE_PARTICLES + snowfall * 120),
    ),
    pointSize: weightedValue(weights, POINT_SIZES),
    fallSpeed: weightedValue(weights, FALL_SPEEDS),
    // At timberline this is fast enough to read as blowing spindrift; the
    // sheltered Lanternwood endpoint remains a gentle sideways drift.
    horizontalSpeed: wind * 12.5,
    opacity: 0.42 + snowfall * 0.65,
    sparkle: weights.blueIce,
    streak: weights.timberline,
    color,
  };
}

function stateFromTarget(target: WeatherTarget): MutableWeatherState {
  return {
    snowfall: target.snowfall,
    wind: target.wind,
    visibleParticles: target.visibleParticles,
    pointSize: target.pointSize,
    fallSpeed: target.fallSpeed,
    horizontalSpeed: target.horizontalSpeed,
    opacity: target.opacity,
    sparkle: target.sparkle,
    streak: target.streak,
    color: new THREE.Color(...target.color),
  };
}

function copyTarget(state: MutableWeatherState, target: WeatherTarget): void {
  state.snowfall = target.snowfall;
  state.wind = target.wind;
  state.visibleParticles = target.visibleParticles;
  state.pointSize = target.pointSize;
  state.fallSpeed = target.fallSpeed;
  state.horizontalSpeed = target.horizontalSpeed;
  state.opacity = target.opacity;
  state.sparkle = target.sparkle;
  state.streak = target.streak;
  state.color.setRGB(...target.color);
}

function approachTarget(state: MutableWeatherState, target: WeatherTarget, amount: number): void {
  state.snowfall += (target.snowfall - state.snowfall) * amount;
  state.wind += (target.wind - state.wind) * amount;
  state.visibleParticles += (target.visibleParticles - state.visibleParticles) * amount;
  state.pointSize += (target.pointSize - state.pointSize) * amount;
  state.fallSpeed += (target.fallSpeed - state.fallSpeed) * amount;
  state.horizontalSpeed += (target.horizontalSpeed - state.horizontalSpeed) * amount;
  state.opacity += (target.opacity - state.opacity) * amount;
  state.sparkle += (target.sparkle - state.sparkle) * amount;
  state.streak += (target.streak - state.streak) * amount;
  state.color.lerp(new THREE.Color(...target.color), amount);
}

function wrap(value: number, extent: number): number {
  const width = extent * 2;
  return ((value + extent) % width + width) % width - extent;
}

const vertexShader = /* glsl */ `
  attribute float particleSize;
  attribute float particlePhase;

  uniform float weatherPointSize;
  uniform float weatherSparkle;
  uniform float weatherTime;

  varying float pointFade;
  varying float pointTwinkle;

  void main() {
    vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
    float depth = max(-viewPosition.z, 0.0);
    float depthScale = 108.0 / max(depth, 10.0);
    float twinkle = 0.62 + 0.38 * (0.5 + 0.5 * sin(weatherTime * 4.2 + particlePhase * 19.0));

    pointTwinkle = mix(1.0, twinkle, weatherSparkle);
    pointFade = 1.0 - smoothstep(46.0, 86.0, depth);
    gl_PointSize = clamp(weatherPointSize * particleSize * depthScale, 1.0, 12.0);
    gl_Position = projectionMatrix * viewPosition;
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 weatherColor;
  uniform float weatherOpacity;
  uniform float weatherSparkle;
  uniform float weatherStreak;

  varying float pointFade;
  varying float pointTwinkle;

  void main() {
    vec2 point = gl_PointCoord - vec2(0.5);
    float disc = 1.0 - smoothstep(0.34, 0.5, length(point));

    float sparkleVertical = (1.0 - smoothstep(0.035, 0.11, abs(point.x)))
      * (1.0 - smoothstep(0.32, 0.5, abs(point.y)));
    float sparkleHorizontal = (1.0 - smoothstep(0.035, 0.11, abs(point.y)))
      * (1.0 - smoothstep(0.32, 0.5, abs(point.x)));
    float sparkle = max(sparkleVertical, sparkleHorizontal);

    float streak = (1.0 - smoothstep(0.055, 0.16, abs(point.y)))
      * (1.0 - smoothstep(0.38, 0.5, abs(point.x)));
    float shape = mix(disc, sparkle, weatherSparkle);
    shape = mix(shape, streak, weatherStreak);
    float alpha = shape * weatherOpacity * pointFade * pointTwinkle;
    if (alpha < 0.015) discard;
    gl_FragColor = vec4(weatherColor, alpha);
  }
`;

/** Adds one lightweight, deterministic particle field to the render origin. */
export function createWeather(scene: THREE.Scene): WeatherController {
  const positions = new Float32Array(WEATHER_PARTICLE_COUNT * 3);
  const baseX = new Float32Array(WEATHER_PARTICLE_COUNT);
  const baseZ = new Float32Array(WEATHER_PARTICLE_COUNT);
  const fallPhase = new Float32Array(WEATHER_PARTICLE_COUNT);
  const flutterPhase = new Float32Array(WEATHER_PARTICLE_COUNT);
  const particleSizes = new Float32Array(WEATHER_PARTICLE_COUNT);

  for (let index = 0; index < WEATHER_PARTICLE_COUNT; index += 1) {
    baseX[index] = (hash2(index, 17, 7_019) * 2 - 1) * PARTICLE_VOLUME_RADIUS;
    baseZ[index] = (hash2(index, 29, 7_043) * 2 - 1) * PARTICLE_VOLUME_RADIUS;
    fallPhase[index] = hash2(index, 43, 7_067);
    flutterPhase[index] = hash2(index, 59, 7_087) * TAU;
    particleSizes[index] = 0.72 + hash2(index, 71, 7_109) * 0.6;
  }

  const geometry = new THREE.BufferGeometry();
  const positionAttribute = new THREE.BufferAttribute(positions, 3);
  positionAttribute.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('position', positionAttribute);
  geometry.setAttribute('particleSize', new THREE.BufferAttribute(particleSizes, 1));
  geometry.setAttribute('particlePhase', new THREE.BufferAttribute(flutterPhase, 1));

  const initialTarget = weatherTarget(biomeWeightsAt(0, -1_200));
  const state = stateFromTarget(initialTarget);
  const uniforms = {
    weatherColor: { value: state.color.clone() },
    weatherOpacity: { value: state.opacity },
    weatherPointSize: { value: state.pointSize },
    weatherSparkle: { value: state.sparkle },
    weatherStreak: { value: state.streak },
    weatherTime: { value: 0 },
  };
  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    toneMapped: false,
  });
  const points = new THREE.Points(geometry, material);
  points.name = 'biome-weather-particles';
  points.frustumCulled = false;
  points.renderOrder = 3;
  scene.add(points);

  let elapsed = 0;
  let horizontalOffset = 0;
  let fallOffset = 0;
  let disposed = false;

  function updatePositions(): void {
    const flutterStrength = 0.38 * (1 - state.streak) + state.wind * 0.14;
    for (let index = 0; index < WEATHER_PARTICLE_COUNT; index += 1) {
      const phase = flutterPhase[index]!;
      const flutter = Math.sin(elapsed * (0.72 + (index % 7) * 0.035) + phase) * flutterStrength;
      const x = wrap(baseX[index]! + horizontalOffset + flutter, PARTICLE_VOLUME_RADIUS);
      const z = wrap(baseZ[index]! - horizontalOffset * 0.12, PARTICLE_VOLUME_RADIUS);
      const progress = (fallPhase[index]! + fallOffset / PARTICLE_VOLUME_HEIGHT) % 1;
      const y = PARTICLE_BOTTOM + PARTICLE_VOLUME_HEIGHT * (1 - progress);
      const positionIndex = index * 3;
      positions[positionIndex] = x;
      positions[positionIndex + 1] = y;
      positions[positionIndex + 2] = z;
    }
    positionAttribute.needsUpdate = true;
  }

  function updateMaterial(): void {
    uniforms.weatherColor.value.copy(state.color);
    uniforms.weatherOpacity.value = state.opacity;
    uniforms.weatherPointSize.value = state.pointSize;
    uniforms.weatherSparkle.value = state.sparkle;
    uniforms.weatherStreak.value = state.streak;
    uniforms.weatherTime.value = elapsed;
    geometry.setDrawRange(0, Math.round(state.visibleParticles));
  }

  updatePositions();
  updateMaterial();

  return {
    update(x: number, z: number, delta = 0, snap = false): void {
      if (disposed) return;
      const target = weatherTarget(biomeWeightsAt(x, z));
      const safeDelta = Math.max(delta, 0);

      if (snap) {
        copyTarget(state, target);
        elapsed = SNAPSHOT_TIME;
        horizontalOffset = SNAPSHOT_TIME * state.horizontalSpeed;
        fallOffset = SNAPSHOT_TIME * state.fallSpeed;
      } else {
        const amount = 1 - Math.exp(-safeDelta * 0.82);
        approachTarget(state, target, amount);
        elapsed += safeDelta;
        horizontalOffset += safeDelta * state.horizontalSpeed;
        fallOffset += safeDelta * state.fallSpeed;
      }

      updatePositions();
      updateMaterial();
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      scene.remove(points);
      geometry.dispose();
      material.dispose();
    },
  };
}
